/* eslint brace-style: ["error", "allman"] */
/**
 * Paged entity search for a pict-section-picker DataProvider: `(term, page) => Promise<{ results, hasMore }>`.
 *
 * One `<Set>/FilteredTo/<filter>/<begin>/<cap>` request per page, where begin is page × page size. Rows are sorted by
 * the sort field and then the ID field, so a name shared by two records cannot shift between pages.
 */

const DEFAULT_PAGE_SIZE = 25;
const MAX_PAGE_SIZE = 250;

/**
 * @typedef {Object} EntityPickerSearchOptions
 * @property {string} Set - The entity set path, e.g. `Labs` or `Projects/Lite`.
 * @property {string} IDField - The record's ID column, e.g. `IDLab`; the tie-break sort.
 * @property {string | (() => string)} [Scope] - FoxHound stanzas ANDed onto every request, e.g.
 *   `FBV~IDCustomer~EQ~10~FBV~Deleted~EQ~0`; a function is read at call time. Default `FBV~Deleted~EQ~0`.
 * @property {string} [SearchField] - The column the typed term is matched against with LIKE (default `Name`).
 * @property {string} [SortField] - The primary sort column, ascending (default `Name`).
 * @property {number} [PageSize] - Records per page (default 25, never above 250).
 * @property {(pRow: Record<string, any>) => { Value: any, Text: string, Record?: Record<string, any> }} ToOption
 * @property {(pRow: Record<string, any>) => boolean} [Include] - Drops rows from the results; `hasMore` still counts
 *   them, so a page the filter empties does not end the paging.
 */

/**
 * @param {EntityPickerSearchOptions} pOptions
 * @param {any} pTerm - What the user typed.
 * @param {any} pPage - Zero-based page index.
 * @returns {string} The request URL, relative to the API root.
 */
function entityPickerSearchURL(pOptions, pTerm, pPage)
{
	const tmpPageSize = Math.min(Math.max(Number(pOptions.PageSize) || DEFAULT_PAGE_SIZE, 1), MAX_PAGE_SIZE);
	const fScope = pOptions.Scope;
	const tmpScope = ((typeof fScope === 'function') ? fScope() : fScope) || 'FBV~Deleted~EQ~0';
	const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
	const tmpSortField = pOptions.SortField || 'Name';
	let tmpFilter = tmpScope;
	if (tmpTerm)
	{
		tmpFilter = `FBV~${pOptions.SearchField || 'Name'}~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`;
	}
	tmpFilter = `${tmpFilter}~FSF~${tmpSortField}~ASC~0`;
	if (tmpSortField !== pOptions.IDField)
	{
		tmpFilter = `${tmpFilter}~FSF~${pOptions.IDField}~ASC~0`;
	}
	const tmpBegin = Math.max(Math.floor(Number(pPage) || 0), 0) * tmpPageSize;
	return `${pOptions.Set}/FilteredTo/${tmpFilter}/${tmpBegin}/${tmpPageSize}`;
}

/**
 * @param {(pURL: string) => Promise<any>} fGetJSON - GET relative to the API root, resolving to the parsed body.
 * @param {EntityPickerSearchOptions} pOptions
 * @returns {(pTerm: any, pPage: number) => Promise<{ results: Array<{ Value: any, Text: string, Record?: Record<string, any> }>, hasMore: boolean }>}
 */
function entityPickerDataProvider(fGetJSON, pOptions)
{
	const tmpPageSize = Math.min(Math.max(Number(pOptions.PageSize) || DEFAULT_PAGE_SIZE, 1), MAX_PAGE_SIZE);
	const fToOption = pOptions.ToOption;
	return (pTerm, pPage) =>
	{
		return Promise.resolve()
			.then(() => fGetJSON(entityPickerSearchURL(pOptions, pTerm, pPage)))
			.then((pRows) =>
			{
				const tmpRows = Array.isArray(pRows) ? pRows : [];
				const tmpKept = (typeof pOptions.Include === 'function') ? tmpRows.filter(pOptions.Include) : tmpRows;
				return { results: tmpKept.map(fToOption), hasMore: (tmpRows.length >= tmpPageSize) };
			})
			.catch(() => ({ results: [], hasMore: false }));
	};
}

module.exports = { entityPickerDataProvider, entityPickerSearchURL, DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE };
